import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { financeAPI } from '../utils/api';
import { formatCurrency } from '../utils/helpers';
import { Calculator, TrendingUp, RotateCcw } from 'lucide-react';

const CALC_FIELDS = [
  { k: 'client_charge', label: 'Client Charge', type: 'revenue' },
  { k: 'crew_cost', label: 'Crew Cost', type: 'cost' },
  { k: 'photo_editing_cost', label: 'Photo Editing', type: 'cost' },
  { k: 'video_editing_cost', label: 'Video Editing', type: 'cost' },
  { k: 'album_cost', label: 'Album Cost', type: 'cost' },
  { k: 'extra_expenses', label: 'Extra Expenses', type: 'cost' },
];

const blankCalc = { client_charge: '', crew_cost: '', photo_editing_cost: '', video_editing_cost: '', album_cost: '', extra_expenses: '' };

const CalculatorTab = () => {
  const [currency, setCurrency] = useState('USD');
  const [vals, setVals] = useState(blankCalc);

  const num = (v) => parseFloat(v) || 0;
  const revenue = num(vals.client_charge);
  const totalCost = ['crew_cost', 'photo_editing_cost', 'video_editing_cost', 'album_cost', 'extra_expenses']
    .reduce((s, k) => s + num(vals[k]), 0);
  const profit = revenue - totalCost;
  const margin = revenue > 0 ? (profit / revenue * 100) : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8" data-testid="calculator-tab">
      <div>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">Enter numbers to estimate profit. Nothing is saved.</p>
          <button
            onClick={() => setVals(blankCalc)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            data-testid="calc-reset"
          >
            <RotateCcw size={13} /> Clear
          </button>
        </div>
        <div className="inline-flex gap-1 mb-5 bg-secondary w-fit rounded-full p-1 overflow-hidden">
          {['USD', 'INR'].map(c => (
            <button
              key={c}
              onClick={() => setCurrency(c)}
              className={`px-5 py-1.5 rounded-full text-xs font-medium transition-all ${currency === c ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`calc-currency-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="space-y-1">
          {CALC_FIELDS.map(f => (
            <div key={f.k} className="flex items-center justify-between gap-3 py-2 border-b border-border/50">
              <label className={`text-sm ${f.type === 'revenue' ? 'font-medium' : 'text-muted-foreground'}`}>{f.label}</label>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">{currency === 'INR' ? '₹' : '$'}</span>
                <input
                  type="number" min="0" step="0.01" placeholder="0"
                  value={vals[f.k]}
                  onChange={(e) => setVals(v => ({ ...v, [f.k]: e.target.value }))}
                  className="w-32 px-2 py-1 text-sm text-right border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground bg-background focus:border-gold focus:outline-none"
                  data-testid={`calc-${f.k}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="card p-6 bg-muted/30 sticky top-6">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Estimate</p>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Total Revenue</span>
              <span className="text-sm" data-testid="calc-revenue">{formatCurrency(revenue, currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Total Costs</span>
              <span className="text-sm text-red-600 dark:text-red-400" data-testid="calc-total-cost">− {formatCurrency(totalCost, currency)}</span>
            </div>
            <div className="border-t border-border pt-4 flex justify-between items-end">
              <span className="text-sm font-medium">Profit</span>
              <span className={`text-3xl font-serif ${profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} data-testid="calc-profit">
                {formatCurrency(profit, currency)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-muted-foreground">Profit Margin</span>
              <span className={`text-sm font-medium ${profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} data-testid="calc-margin">
                {margin.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CurrencyOverview = ({ label, symbol, totals, projects }) => {
  if (!totals || totals.project_count === 0) return null;
  // group projects by year
  const byYear = {};
  projects.forEach(p => { (byYear[p.year] = byYear[p.year] || []).push(p); });
  const years = Object.keys(byYear).sort().reverse();

  return (
    <div className="mb-10" data-testid={`overview-${label}`}>
      <h3 className="text-lg font-serif mb-4">{label} · {totals.project_count} project{totals.project_count !== 1 ? 's' : ''}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Revenue</p>
          <p className="text-lg font-serif">{formatCurrency(totals.client_charge, label)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Total Costs</p>
          <p className="text-lg font-serif text-red-600 dark:text-red-400">{formatCurrency(totals.total_cost, label)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Net Profit</p>
          <p className={`text-lg font-serif ${totals.profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{formatCurrency(totals.profit, label)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Avg Margin</p>
          <p className={`text-lg font-serif ${totals.margin >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{totals.margin}%</p>
        </div>
      </div>

      {years.map(year => (
        <div key={year} className="mb-6">
          <p className="text-sm font-medium text-muted-foreground mb-2">{year}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4">Project</th>
                  <th className="py-2 px-2 text-right">Charge</th>
                  <th className="py-2 px-2 text-right">Costs</th>
                  <th className="py-2 px-2 text-right">Profit</th>
                  <th className="py-2 pl-2 text-right">Margin</th>
                </tr>
              </thead>
              <tbody>
                {byYear[year].map(p => (
                  <tr key={p.project_id} className="border-b border-border/40" data-testid={`overview-row-${p.project_id}`}>
                    <td className="py-2 pr-4">
                      <span className="font-medium">{p.project_name}</span>
                      <span className="block text-xs text-muted-foreground">{p.client_name}</span>
                    </td>
                    <td className="py-2 px-2 text-right">{formatCurrency(p.client_charge, label)}</td>
                    <td className="py-2 px-2 text-right text-red-600 dark:text-red-400">{formatCurrency(p.total_cost, label)}</td>
                    <td className={`py-2 px-2 text-right font-medium ${p.profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{formatCurrency(p.profit, label)}</td>
                    <td className={`py-2 pl-2 text-right ${p.profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{p.margin}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

const OverviewTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    financeAPI.overview()
      .then(r => setData(r.data))
      .catch(() => setData({ projects: [], totals: { USD: { project_count: 0 }, INR: { project_count: 0 } } }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4" data-testid="overview-loading">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map(i => <div key={i} className="card p-6 h-24 animate-pulse" />)}
      </div>
      <div className="card p-6 h-48 animate-pulse" />
    </div>
  );

  const usdProjects = (data.projects || []).filter(p => p.currency === 'USD');
  const inrProjects = (data.projects || []).filter(p => p.currency === 'INR');
  const hasAny = (data.totals.USD.project_count || 0) > 0 || (data.totals.INR.project_count || 0) > 0;

  if (!hasAny) {
    return (
      <div className="card p-10 text-center text-sm text-muted-foreground" data-testid="overview-empty">
        No project finances entered yet. Add revenue and costs on a project's <strong>Finances</strong> panel to see profit totals here.
      </div>
    );
  }

  return (
    <div data-testid="overview-tab">
      <CurrencyOverview label="USD" symbol="$" totals={data.totals.USD} projects={usdProjects} />
      <CurrencyOverview label="INR" symbol="₹" totals={data.totals.INR} projects={inrProjects} />
    </div>
  );
};

const Finance = () => {
  const [tab, setTab] = useState('calculator');

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="finance-page">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-serif mb-1 sm:mb-2">Finance</h1>
          <p className="text-sm text-muted-foreground">Estimate deals and track profit across your projects</p>
        </div>

        <div className="flex gap-1 mb-6 border-b border-border">
          <button
            onClick={() => setTab('calculator')}
            className={`px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${tab === 'calculator' ? 'border-b-2 border-black text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="finance-tab-calculator"
          >
            <Calculator size={14} /> Calculator
          </button>
          <button
            onClick={() => setTab('overview')}
            className={`px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${tab === 'overview' ? 'border-b-2 border-black text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="finance-tab-overview"
          >
            <TrendingUp size={14} /> Company Overview
          </button>
        </div>

        {tab === 'calculator' ? <CalculatorTab /> : <OverviewTab />}
      </div>
    </Layout>
  );
};

export default Finance;
